import React, { useMemo } from "react";

import { DirectionsBoatOutlined as DirectionsBoatOutlinedIcon } from "@mui/icons-material";
import classnames from "classnames";
import { useTranslation } from "react-i18next";
import { CgHome } from "react-icons/cg";
import { IoAirplaneOutline } from "react-icons/io5";
import { RiTruckLine } from "react-icons/ri";
import { TiWarningOutline } from "react-icons/ti";

import { Destination } from "../../../../models/destination";
import { JobMode } from "../../../../models/job.mode";
import { Location } from "../../../../models/location";
import { RoomJob, ItemJob, QuotedItem } from "../../../../models/sales";
import { convertMetricVolume, handleGetConvertValue } from "../../../../util/converts";
import { DESTINATION, ITEM_DESTINATION_COLOR, Mode } from "../../../../util/enums";
import { getMeasurement, getWeightMeasurement } from "../../../../util/getMeasurement";
import useStyles, { styleComponentItem } from "./styles";

interface InfoSectionProps {
  inventory: RoomJob[];
  modes: JobMode[];
  destinationList?: Destination[];
  destinationAddresses?: Location[];
}

const InfoSection = ({
  inventory,
  modes,
  destinationAddresses,
  destinationList,
}: InfoSectionProps) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const classesComponent = styleComponentItem();

  const measurement = getMeasurement();
  const measurementWeight = getWeightMeasurement();

  const modeOptions: string[] = useMemo(
    () => [Mode.Road, Mode.Air, Mode.Sea, DESTINATION.NOT_GOING],
    []
  );

  const getItemCounts = (items: QuotedItem[]) => {
    let quantity = 0;
    let volume = 0;
    let weight = 0;

    items.forEach((item) => {
      quantity += Number((item?.itemJob as ItemJob).quantity) || 0;
      volume +=
        Number(
          convertMetricVolume(Number((item?.itemJob as ItemJob).volume), measurement?.code, 1)
        ) * Number((item?.itemJob as ItemJob).quantity) || 0;
      weight += Number(
        handleGetConvertValue(
          Number((item?.itemJob as ItemJob).weight || 0),
          measurementWeight?.code || "kg",
          measurementWeight?.decimalPlaces
        )
      );
    });

    return { quantity, volume, weight };
  };

  const addressDestinationItems = useMemo(() => {
    const destinationsId = destinationAddresses?.map((address) => address.id);
    const destinationItems: { [x: string]: QuotedItem[] } = {};

    destinationsId?.forEach((destinationId) => {
      if (destinationId) {
        const items: QuotedItem[] = [];

        inventory?.forEach((inventoryValue) => {
          inventoryValue?.itemsJob?.forEach((itemJob) => {
            if (
              itemJob &&
              itemJob.itemJob &&
              itemJob.itemJob.destinationResidenceId &&
              itemJob.itemJob.destinationResidenceId === destinationId
            ) {
              items.push(itemJob);
            }
          });

          inventoryValue?.materials?.forEach((material) => {
            if (
              material &&
              material?.serviceBooking?.isItem &&
              material.destinationResidenceId &&
              material.destinationResidenceId === destinationId
            ) {
              const itemJob = {
                itemJob: material,
              } as QuotedItem;

              items.push(itemJob);
            }
          });
        });
        if (items && items.length > 0) {
          destinationItems[`id-${destinationId.toString()}`] = items;
        }
      }
    });

    return destinationItems;
  }, [destinationAddresses, inventory]);

  const customDestinationItems = useMemo(() => {
    const destinationsId = destinationList
      ?.filter((destination) => !destination.name?.includes(DESTINATION.NOT_GOING))
      .map((destination) => destination.id);

    const destinationItems: { [x: string]: QuotedItem[] } = {};

    destinationsId?.forEach((destinationId) => {
      if (destinationId) {
        const items: QuotedItem[] = [];

        inventory?.forEach((inventoryValue) => {
          inventoryValue?.itemsJob?.forEach((itemJob) => {
            if (itemJob && itemJob.itemJob && itemJob.itemJob.destinationId === destinationId) {
              items.push(itemJob);
            }
          });
          inventoryValue?.materials?.forEach((material) => {
            if (
              material &&
              material?.serviceBooking?.isItem &&
              material.destinationId &&
              material.destinationId === destinationId
            ) {
              const itemJob = {
                itemJob: material,
              } as QuotedItem;

              items.push(itemJob);
            }
          });
        });

        if (items && items.length > 0) {
          destinationItems[`id-${destinationId.toString()}`] = items;
        }
      }
    });

    return destinationItems;
  }, [destinationList, inventory]);

  const customModeDestinationItems = useMemo(() => {
    const modeDestinationsId = modes
      ?.filter((modeDestination) => modeDestination.isDestination)
      .map((modeDestination) => modeDestination.id);

    const modeDestinationItems: { [x: string]: QuotedItem[] } = {};

    modeDestinationsId?.forEach((modeDestinationId) => {
      if (modeDestinationId) {
        const items: QuotedItem[] = [];

        inventory?.forEach((inventoryValue) => {
          inventoryValue?.itemsJob?.forEach((itemJob) => {
            if (
              itemJob &&
              itemJob.itemJob &&
              itemJob.itemJob?.modeDestinationId === modeDestinationId
            ) {
              items.push(itemJob);
            }
          });
          inventoryValue?.materials?.forEach((material) => {
            if (
              material &&
              material?.serviceBooking?.isItem &&
              material?.modeDestinationId === modeDestinationId
            ) {
              const itemJob = {
                itemJob: material,
              } as QuotedItem;

              items.push(itemJob);
            }
          });
        });

        if (items && items.length > 0) {
          modeDestinationItems[`id-${modeDestinationId.toString()}`] = items;
        }
      }
    });

    return modeDestinationItems;
  }, [modes, inventory]);

  const calculateByDestination = (mode?: string, destination?: string) => {
    let quantity = 0;
    let volume = 0;
    let weight = 0;

    inventory.forEach((room: RoomJob) => {
      if (room?.materials && room?.materials?.length > 0) {
        room.materials.forEach((material) => {
          if (material?.serviceBooking?.isItem) {
            if (destination) {
              if (material?.destination?.name?.toLowerCase() === destination?.toLowerCase()) {
                quantity += Number(material?.quantity);
                volume +=
                  Number(convertMetricVolume(Number(material?.volume), measurement?.code, 1)) *
                  Number(material?.quantity);
                weight +=
                  Number(
                    handleGetConvertValue(
                      Number(material?.weight || 0),
                      measurementWeight?.code || "kg",
                      measurementWeight?.decimalPlaces
                    )
                  ) * Number(material?.quantity);
              }
            } else if (mode) {
              if (
                material?.mode?.toLowerCase() === mode?.toLowerCase() &&
                material?.destination?.name?.toLowerCase() !== DESTINATION.NOT_GOING?.toLowerCase()
              ) {
                quantity += Number(material?.quantity);
                volume +=
                  Number(convertMetricVolume(Number(material?.volume), measurement?.code, 1)) *
                  Number(material?.quantity);
                weight +=
                  Number(
                    handleGetConvertValue(
                      Number(material?.weight || 0),
                      measurementWeight?.code || "kg",
                      measurementWeight?.decimalPlaces
                    )
                  ) * Number(material?.quantity);
              }
            }
          }
        });
      }

      if (room?.itemsJob && room?.itemsJob?.length > 0) {
        room.itemsJob.forEach((item: QuotedItem) => {
          if (destination) {
            if (item?.itemJob?.destination?.name?.toLowerCase() === destination?.toLowerCase()) {
              quantity += Number(item?.itemJob?.quantity);
              volume +=
                Number(convertMetricVolume(Number(item?.itemJob?.volume), measurement?.code, 1)) *
                Number(item?.itemJob?.quantity);
              weight +=
                Number(
                  handleGetConvertValue(
                    Number(item?.itemJob?.weight || 0),
                    measurementWeight?.code || "kg",
                    measurementWeight?.decimalPlaces
                  )
                ) * Number(item?.itemJob?.quantity);
            }
          } else if (mode) {
            if (
              item.itemJob.mode?.toLowerCase() === mode?.toLowerCase() &&
              item?.itemJob?.destination?.name?.toLowerCase() !==
                DESTINATION.NOT_GOING?.toLowerCase()
            ) {
              quantity += Number(item?.itemJob?.quantity);
              volume +=
                Number(convertMetricVolume(Number(item?.itemJob?.volume), measurement?.code, 1)) *
                Number(item?.itemJob?.quantity);
              weight +=
                Number(
                  handleGetConvertValue(
                    Number(item?.itemJob?.weight || 0),
                    measurementWeight?.code || "kg",
                    measurementWeight?.decimalPlaces
                  )
                ) * Number(item?.itemJob?.quantity);
            }
          }
        });
      }
    });

    return {
      quantity,
      volume: Number(volume),
      weight: Number(weight),
    };
  };

  const renderSection = (props: {
    title: string;
    quantity: number;
    volume: number;
    weight: number;
    icon?: () => JSX.Element | null;
    color?: string;
    titleClassName?: string;
  }) => {
    const { title, quantity, volume, weight, icon, color, titleClassName = "" } = props;

    if (quantity && quantity > 0) {
      return (
        <div
          className={`${classes.infoComponentItem} ${
            !modeOptions.includes(title) ? classesComponent.itemIcon : ""
          }`}
        >
          <div
            className={classnames(
              classes.infoComponentItemHeader,
              !modeOptions.includes(title) ? classesComponent.itemIconHeader : ""
            )}
          >
            {icon && icon()}
            <p
              className={classnames(titleClassName || classes.colorIcon, classes.info)}
              style={{ color }}
              data-cy={`info-section-title-${title}`}
            >
              {title}
            </p>
          </div>

          <ul className={classes.infoComponentItemList}>
            <li>
              <span className={classes.title}>{translate("items")}</span>
              <span className={classnames(classes.colorQuantity, classes.info)}>{quantity}</span>
            </li>

            <li>
              <span className={classes.title}>{translate("volume")}</span>

              <span className={classnames(classes.colorQuantity, classes.info)}>
                {volume?.toFixed(2)}

                {` ${measurement?.code || ""}`}
              </span>
            </li>
            <li>
              <span className={classes.title}>{translate("weight")}</span>
              <span className={classnames(classes.colorQuantity, classes.info)}>
                {weight?.toFixed(2)}
                {` ${measurementWeight?.code || ""}`}
              </span>
            </li>
          </ul>
        </div>
      );
    }

    return null;
  };

  const returnModeIcon = (modeName?: string) => {
    switch (modeName) {
      case Mode.Road:
        return <RiTruckLine color="#308CDB" />;
      case Mode.Sea:
        return <DirectionsBoatOutlinedIcon htmlColor="#308CDB" />;
      case Mode.Air:
        return <IoAirplaneOutline color="#308CDB" />;
      case Mode.Warning:
        return <TiWarningOutline color="#F97066" />;
      default:
        return null;
    }
  };

  const {
    quantity: quantityNotGoing,
    volume: volumeNotGoing,
    weight: weightNotGoing,
  } = calculateByDestination(undefined, DESTINATION.NOT_GOING);

  return (
    <div className={classes.infoComponent}>
      {Object.keys(addressDestinationItems).map((addressId) => {
        const address = destinationAddresses?.find(
          (addressItem) => String(addressItem.id) === String(addressId?.replace("id-", ""))
        );
        const items = addressDestinationItems[addressId];

        if (!address || !items) return null;

        return renderSection({
          title: translate("residence"),
          ...getItemCounts(items),
          icon: () => <CgHome color={ITEM_DESTINATION_COLOR.Residence} />,
          color: ITEM_DESTINATION_COLOR.Residence,
        });
      })}

      {Object.keys(customDestinationItems).map((destinationId) => {
        const destination = destinationList?.find(
          (destination) => String(destination.id) === String(destinationId?.replace("id-", ""))
        );

        const items = customDestinationItems[destinationId];

        if (!destination || !items) return null;

        return renderSection({
          title: destination?.name,
          color: destination?.color,
          ...getItemCounts(items),
        });
      })}

      {Object.keys(customModeDestinationItems).map((modeDestinationId) => {
        const modeDestination = modes?.find(
          (modeDestination) =>
            String(modeDestination.id) === String(modeDestinationId?.replace("id-", ""))
        );
        const items = customModeDestinationItems[modeDestinationId];

        if (!modeDestination || !items) return null;

        return renderSection({
          title: modeDestination.name,
          color: modeDestination?.color,
          ...getItemCounts(items),
        });
      })}

      {renderSection({
        title: DESTINATION.NOT_GOING,
        quantity: quantityNotGoing,
        volume: volumeNotGoing,
        weight: weightNotGoing,
        icon: () => returnModeIcon("Warning"),
        titleClassName: classes.colorNotGoing,
      })}
    </div>
  );
};

export default InfoSection;
